import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { format } from 'date-fns';
import { MdComputer, MdSmartphone, MdVideocam, MdHeadset, MdMouse, MdKeyboard, MdWarning, MdCheckCircle, MdError, MdSchedule, MdPerson, MdEngineering } from 'react-icons/md';
import EngineerVisitStatus from './EngineerVisitStatus';

const HardwareReportForm = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        total_computers: '', working_computers: '',
        total_mobiles: '', working_mobiles: '',
        total_cctv: '', working_cctv: '',
        total_headphones: '', working_headphones: '',
        total_mouse: '', working_mouse: '',
        total_keyboards: '', working_keyboards: '',
        remarks: '',
        is_urgent: false,
    });

    useEffect(() => {
        fetchReports();
    }, []);

    const fetchReports = async () => {
        try {
            const res = await api.get('/hardware/reports');
            // Check if report exists for today
            const today = format(new Date(), 'yyyy-MM-dd');
            const todaysReport = res.data.find(r => r.report_date === today);
            setStats(todaysReport);
        } catch (error) {
            console.error("Failed to fetch reports", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            // Convert to numbers to avoid validation issues with strings/empty fields
            const processedData = {};
            const numericFields = [
                'total_computers', 'working_computers', 'total_mobiles', 'working_mobiles',
                'total_cctv', 'working_cctv', 'total_headphones', 'working_headphones',
                'total_mouse', 'working_mouse', 'total_keyboards', 'working_keyboards'
            ];

            Object.keys(formData).forEach(key => {
                if (numericFields.includes(key)) {
                    processedData[key] = parseInt(formData[key]) || 0;
                } else {
                    processedData[key] = formData[key];
                }
            });

            const data = {
                ...processedData,
                report_date: format(new Date(), 'yyyy-MM-dd')
            };

            await api.post('/hardware/reports', data);

            // Reset form fields after successful submission
            setFormData({
                total_computers: '', working_computers: '',
                total_mobiles: '', working_mobiles: '',
                total_cctv: '', working_cctv: '',
                total_headphones: '', working_headphones: '',
                total_mouse: '', working_mouse: '',
                total_keyboards: '', working_keyboards: '',
                remarks: '',
                is_urgent: false,
            });

            fetchReports(); // Refresh
            alert("Report Submitted Successfully!");
        } catch (error) {
            console.error(error);
            const msg = error.response?.data?.message || "Failed to submit report. Please check if working units <= total units.";
            alert(`Error: ${msg}`);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="space-y-6">
            {/* 1. Show Engineer Visit Status ALWAYS at the top */}
            <EngineerVisitStatus />

            {/* 2. Then show either the Submitted View OR the Form */}
            {stats ? (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-green-200">
                    <div className="flex items-center gap-3 text-green-700 mb-4">
                        <MdCheckCircle size={32} />
                        <div>
                            <h3 className="text-lg font-bold">Daily Report Submitted</h3>
                            <p className="text-sm">You have already submitted the hardware status for today.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <StatBox label="Computers" total={stats.total_computers} working={stats.working_computers} icon={MdComputer} />
                        <StatBox label="Mobiles" total={stats.total_mobiles} working={stats.working_mobiles} icon={MdSmartphone} />
                        <StatBox label="CCTV" total={stats.total_cctv} working={stats.working_cctv} icon={MdVideocam} />
                        <StatBox label="Headphones" total={stats.total_headphones} working={stats.working_headphones} icon={MdHeadset} />
                        <StatBox label="Mouse" total={stats.total_mouse} working={stats.working_mouse} icon={MdMouse} />
                        <StatBox label="Keyboards" total={stats.total_keyboards} working={stats.working_keyboards} icon={MdKeyboard} />
                    </div>
                    {stats.is_urgent ? (
                        <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 font-bold border border-red-200">
                            <MdWarning size={20} /> Urgent Repair Requested
                        </div>
                    ) : (
                        <div className="mt-4 p-3 bg-gray-50 text-gray-600 rounded-lg text-sm italic">
                            No urgent repairs requested.
                        </div>
                    )}
                    {stats.remarks && (
                        <div className="mt-4 p-3 bg-gray-50 text-gray-700 rounded-lg text-sm">
                            <strong>Remarks:</strong> {stats.remarks}
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                    <h2 className="text-xl font-bold text-slate-800 mb-2">Daily Hardware Status</h2>
                    <p className="text-sm text-slate-500 mb-6">Please update the status of hardware in your center for today.</p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputGroup label="Computers" icon={MdComputer} nameTotal="total_computers" nameWorking="working_computers" formData={formData} onChange={handleChange} />
                            <InputGroup label="Mobiles" icon={MdSmartphone} nameTotal="total_mobiles" nameWorking="working_mobiles" formData={formData} onChange={handleChange} />
                            <InputGroup label="CCTV Cameras" icon={MdVideocam} nameTotal="total_cctv" nameWorking="working_cctv" formData={formData} onChange={handleChange} />
                            <InputGroup label="Headphones" icon={MdHeadset} nameTotal="total_headphones" nameWorking="working_headphones" formData={formData} onChange={handleChange} />
                            <InputGroup label="Mouse" icon={MdMouse} nameTotal="total_mouse" nameWorking="working_mouse" formData={formData} onChange={handleChange} />
                            <InputGroup label="Keyboards" icon={MdKeyboard} nameTotal="total_keyboards" nameWorking="working_keyboards" formData={formData} onChange={handleChange} />
                        </div>

                        <div className="pt-4 border-t border-slate-100">
                            <label className="flex items-center gap-3 p-4 border border-red-200 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors">
                                <input
                                    type="checkbox"
                                    name="is_urgent"
                                    checked={formData.is_urgent}
                                    onChange={handleChange}
                                    className="w-5 h-5 text-red-600 rounded focus:ring-red-500 border-gray-300"
                                />
                                <div className="flex-1">
                                    <span className="font-bold text-red-800 flex items-center gap-2"><MdError /> Urgent Repair Needed</span>
                                    <span className="text-xs text-red-600">Check this if any hardware requires immediate attention from an engineer.</span>
                                </div>
                            </label>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Remarks / Issues Description</label>
                            <textarea
                                name="remarks"
                                rows="3"
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                placeholder="Describe any specific issues e.g., 'PC-4 monitor flickering', '2 headphones broken'..."
                                value={formData.remarks}
                                onChange={handleChange}
                            ></textarea>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                            >
                                {submitting ? 'Submitting...' : 'Submit Daily Report'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

const InputGroup = ({ label, icon: Icon, nameTotal, nameWorking, formData, onChange }) => (
    <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
        <div className="flex items-center gap-2 mb-3 text-slate-700 font-medium">
            <Icon size={20} />
            {label}
        </div>
        <div className="flex gap-4">
            <div className="flex-1">
                <label className="text-xs text-slate-500 block mb-1">Total</label>
                <input
                    type="number"
                    name={nameTotal}
                    min="0"
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-indigo-500 outline-none"
                    value={formData[nameTotal]}
                    onChange={onChange}
                />
            </div>
            <div className="flex-1">
                <label className="text-xs text-slate-500 block mb-1">Working</label>
                <input
                    type="number"
                    name={nameWorking}
                    min="0"
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-indigo-500 outline-none"
                    value={formData[nameWorking]}
                    onChange={onChange}
                />
            </div>
        </div>
    </div>
);

const StatBox = ({ label, total, working, icon: Icon }) => (
    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-center">
        <div className="flex justify-center text-slate-400 mb-1"><Icon size={24} /></div>
        <div className="text-xs text-slate-500 uppercase font-bold">{label}</div>
        <div className={`text-lg font-bold ${working < total ? 'text-red-600' : 'text-green-600'}`}>
            {working}/{total}
        </div>
    </div>
);



export default HardwareReportForm;
